import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const headerBg = scrolled || !isHomePage
    ? 'bg-white/95 backdrop-blur-md shadow-lg'
    : 'bg-transparent';

  const textColor = scrolled || !isHomePage ? 'text-gray-900' : 'text-white';
  const subTextColor = scrolled || !isHomePage ? 'text-gray-600' : 'text-white/80';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex flex-col group">
            <span
              className={`text-xl sm:text-2xl font-bold transition-colors duration-300 ${textColor}`}
              style={{ fontFamily: 'cursive' }}
            >
              Barham
            </span>
            <span className={`text-[10px] sm:text-xs tracking-widest uppercase font-bold transition-colors duration-300 ${subTextColor}`}>
              DRIVER
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4 lg:gap-6">
            {/* Destinations link */}
            <Link
              to="/destinations"
              className={`text-sm font-medium transition-colors duration-300 ${textColor} hover:opacity-80`}
            >
              Destinations
            </Link>
            {/* Phone number */}
            <a
              href="tel:+221774884254"
              className={`flex items-center gap-2 text-sm transition-colors duration-300 ${textColor} hover:opacity-80`}
            >
              <Phone size={16} />
              <span className="hidden lg:inline">+221 77 488 42 54</span>
            </a>
            {/* Request a quote button */}
            <Link
              to="/contact"
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 transform hover:scale-105 ${scrolled || !isHomePage
                ? 'bg-gray-900 text-white hover:bg-gray-800'
                : 'bg-white text-gray-900 hover:bg-white/90'
                }`}
            >
              Demander un devis
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            className={`md:hidden p-2 rounded-full transition-all duration-300 ${textColor} ${scrolled || !isHomePage ? 'hover:bg-gray-100' : 'hover:bg-white/20'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <div className="relative w-6 h-6">
              <Menu
                size={24}
                className={`absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`}
              />
              <X
                size={24}
                className={`absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation - Beautiful slide down panel */}
      <div
        className={`md:hidden fixed left-4 right-4 top-20 transition-all duration-500 ease-out transform ${mobileMenuOpen
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
        style={{ zIndex: 100 }}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
          {/* Decorative top gradient bar */}
          <div className="h-1 bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900" />

          <nav className="flex flex-col p-5 gap-3">
            {/* Phone number with icon */}
            <a
              href="tel:+221774884254"
              className="flex items-center gap-4 py-4 px-4 text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Phone size={18} className="text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Appelez-nous</span>
                <span className="font-semibold text-gray-900">+221 77 488 42 54</span>
              </div>
            </a>

            {/* Separator */}
            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-1" />

            {/* Primary CTA button */}
            <Link
              to="/contact"
              className="py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white text-center rounded-xl font-semibold hover:from-gray-800 hover:to-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
              onClick={() => setMobileMenuOpen(false)}
            >
              ✨ Demander un devis
            </Link>

            {/* Secondary link */}
            <Link
              to="/fleet"
              className="py-3 text-gray-600 text-center hover:text-gray-900 font-medium transition-colors rounded-xl hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              Notre véhicule
            </Link>
            <Link
              to="/destinations"
              className="py-3 text-gray-600 text-center hover:text-gray-900 font-medium transition-colors rounded-xl hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              🗺️ Destinations
            </Link>
          </nav>

          {/* Decorative bottom */}
          <div className="px-5 pb-4">
            <div className="text-center text-xs text-gray-400">
              🇸🇳 Service VTC au Sénégal
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop overlay */}
      <div
        className={`md:hidden fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        style={{ zIndex: 40, top: '64px' }}
        onClick={() => setMobileMenuOpen(false)}
      />
    </header>
  );
};

export default Header;
